"use client";

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { normalizeSearchQuery } from "../../utils/search";

export type SearchableOption = {
  value: string;
  label: string;
  /** Optional custom row content (e.g. color thumbnail) */
  node?: React.ReactNode;
};

type SearchableSelectProps = {
  value?: string;
  options: SearchableOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  /** Show selected option node in the trigger when available */
  showSelectedNode?: boolean;
};

const SEARCH_BAR_H = 52;
const MAX_MENU_H = 320;
const MIN_LIST_H = 120;

/**
 * Scrollable dropdown with in-menu search.
 * Menu is portaled to document.body so drawer overflow cannot clip options.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value = "",
  options,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className,
  triggerClassName,
  disabled = false,
  showSelectedNode = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [listMaxH, setListMaxH] = useState(220);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = normalizeSearchQuery(query).toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.value}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const updateMenuPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    const available = Math.max(MIN_LIST_H + SEARCH_BAR_H, openUp ? spaceAbove : spaceBelow);
    const totalH = Math.min(MAX_MENU_H, available);
    const listH = Math.max(MIN_LIST_H, totalH - SEARCH_BAR_H);
    setListMaxH(listH);
    setMenuStyle({
      position: "fixed",
      left: Math.max(8, Math.min(rect.left, window.innerWidth - Math.max(rect.width, 200) - 8)),
      width: Math.max(rect.width, 200),
      zIndex: 10050,
      height: totalH,
      maxHeight: totalH,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="bg-white border-2 border-teal-300 rounded-lg shadow-2xl flex flex-col overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-200 shrink-0 bg-white">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Explicit max-height so overflow-y-auto always scrolls when options overflow */}
          <div
            ref={listRef}
            className="overflow-y-auto overscroll-contain bg-white"
            style={{
              maxHeight: listMaxH,
              height: listMaxH,
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
            }}
            onWheel={(e) => {
              // Keep wheel scroll inside the list (don't scroll the drawer behind)
              e.stopPropagation();
            }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No matches</div>
            ) : (
              filtered.map((opt, idx) => {
                const active = opt.value === value;
                return (
                  <button
                    key={`${opt.value}-${idx}`}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 border-b border-gray-100 last:border-b-0 hover:bg-teal-50 transition-colors shrink-0",
                      active && "bg-teal-50"
                    )}
                  >
                    <span className="flex-1 min-w-0 flex items-center gap-2">
                      {opt.node || null}
                      <span className="truncate font-medium text-gray-900">{opt.label}</span>
                    </span>
                    {active ? <Check size={16} className="text-teal-600 shrink-0" /> : null}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full px-3 py-2 border-2 rounded-lg bg-white text-left flex items-center justify-between gap-2 font-semibold text-gray-700 hover:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:opacity-50",
          triggerClassName
        )}
      >
        <span className="flex items-center gap-2 min-w-0 text-sm">
          {showSelectedNode && selected?.node ? selected.node : null}
          <span className="truncate">{selected?.label || placeholder}</span>
        </span>
        <ChevronDown
          size={16}
          className={cn("shrink-0 text-gray-500 transition-transform", open && "rotate-180")}
        />
      </button>
      {menu}
    </div>
  );
};

export default SearchableSelect;
